import React, {
  useEffect,
  useContext,
  useState,
} from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { ReactSVG } from 'react-svg';
import { useHistory, useParams } from 'react-router';
import getConfig from '../../config';
import {
  handleResponseStatus,
  sendRequest,
  checkExistingFieldAgent,
} from '../../utils/api-request';
import {
  checkExistingDataUpload,
  getCurrentProjectStage,
  getProjectReference,
} from '../../utils/helpers';
import {
  ACTIVE_STATUS,
  REGISTER_STATUS,
  PAUSED_STATUS,
  FAILED_STATUS,
  CLOSED_STATUS, TERMINATED_STATUS,
} from '../../constans/constans';
import { GAS } from '../../state/near';
import {
  getContract, contractMethods, getTxOutcome, isWalletCancelError,
} from '../../utils/near-utils';
import { appStore } from '../../state/app';
import { buildGatewayUrl, initIPFS } from '../../state/ipfs';
import CustomBtn from '../../generic/CustomBtn';
import headerLogo from '../../assets/image/logo.svg';
import Footer from '../../Layouts/Layout/Footer/Footer';
import SubzoneModal from '../../page/ProjectPage/components/SubzoneModal/SubzoneModal';
import MonitoringZone from './components/MonitoringZone';
import SampleZones from './components/SampleZones';
import StepWizard from '../WizardForm/Steps/StepWizard';
import Loader from '../Loader';
import RequestModal from '../RequestModal/RequestModal';
import { queueToastifyMessage } from '../Toastify/Toastify';

const { indexerApi, deposit } = getConfig();

const DataUploadWizard = () => {
  const history = useHistory();
  const { state } = useContext(appStore);
  const { account } = state;
  const translate = useIntl();

  const currentDate = Date.now() * 1e6;

  const { nameId } = useParams();

  const [totalData, setTotalData] = useState({});
  const [projectStatus, setProjectStatus] = useState('');
  const [step, setStep] = useState(0);
  const [loadingData, setLoading] = useState(true);
  const [currentStage, setCurrentStage] = useState();
  const [numberSampleZones, setNumberSampleZones] = useState(0);
  const [fieldAgentExist, setFieldAgentExist] = useState(false);
  const [lastStageEndTime, setLastStageEndTime] = useState(0);

  const [projectReportType, setProjectReportType] = useState('');
  const [firstDataUpload, setFirstDataUpload] = useState(false);

  const [showRequestModal, setShowRequestModal] = useState(false);
  const [existingReportModal, setExistingReportModal] = useState(false);

  const stepForm = [
    { id: 'uploadWizard.monitoringZone' },
    { id: 'uploadWizard.samplePlots' },
  ];

  const nextPage = () => {
    setStep(step + 1);
    window.scrollTo(0, 0);
  };

  const prevPage = () => {
    setStep(step - 1);
    window.scrollTo(0, 0);
  };

  const defineDataUpload = (data, currStage) => {
    if (data?.stagesInfo?.length) {
      const firstData = { ...data.stagesInfo[0] };
      const isFirst = firstData.stage === currStage;
      if (isFirst) {
        setFirstDataUpload(true);
        setProjectReportType(data.projectReportType);
      } else {
        setFirstDataUpload(false);
        setProjectReportType(data.projectReportType);
      }
    } else {
      setFirstDataUpload(true);
      setProjectReportType('');
    }
  };

  const failFunc = () => {
    setLoading(false);
    setShowRequestModal(true);
  };

  const updateProject = async (cid) => {
    const { project_id: projectId, name, iconCidDir, type } = totalData;

    const contract = getContract(account, contractMethods, 0);
    const prevReference = await getProjectReference(contract, projectId);
    if (cid && prevReference) {
      const txResult = await contract.update_project_metadata({
        project_id: projectId,
        project_type: type,
        title: name,
        media: buildGatewayUrl(iconCidDir),
        reference: `/ipfs/${cid}`,
        prev_reference: prevReference,
        additional_duration: null,
      }, GAS, deposit);
      const { isSuccess, methodName } = getTxOutcome(txResult);
      if (!isSuccess || methodName !== 'update_project_metadata') {
        if (isWalletCancelError(txResult)) {
          return;
        }
        throw new Error('update_project_metadata tx was not successful');
      }
      try {
        if (nameId && projectStatus === ACTIVE_STATUS) {
          localStorage.setItem(`ofp_mz_pending_${nameId}`, 'true');
        }
      } catch (e) {
        // ignore localStorage errors
      }
      queueToastifyMessage(translate.formatMessage({ id: 'smart-contract-notify' }));
      history.push('/');
    } else failFunc();
  };

  const handleMintProject = async () => {
    try {
      const contract = getContract(account, contractMethods, 0);
      const txResult = await contract.update_project_status({
        project_id: totalData.project_id,
        project_status: ACTIVE_STATUS,
      }, GAS, deposit);
      const { isSuccess, methodName } = getTxOutcome(txResult);
      if (!isSuccess || methodName !== 'update_project_status') {
        if (isWalletCancelError(txResult)) {
          return;
        }
        throw new Error('update_project_status tx was not successful');
      }
      queueToastifyMessage(translate.formatMessage({ id: 'smart-contract-notify' }));
      history.push('/');
    } catch (e) {
      if (isWalletCancelError(e)) {
        return;
      }
      failFunc();
    }
  };

  const handleUpdateProject = async (dataProject) => {
    try {
      const ipfs = await initIPFS();
      const { path } = await ipfs.add(JSON.stringify(dataProject));
      if (typeof path === 'string') await updateProject(path);
      else failFunc();
    } catch (e) {
      if (isWalletCancelError(e)) {
        return;
      }
      failFunc();
    }
  };

  useEffect(async () => {
    const successFunc = async (data) => {
      const token = data?.hits?.hits?.length && data?.hits?.hits[0]._source;

      if (token) {
        if (token.stages?.length) setLastStageEndTime(token.stages[token.stages.length - 1].ends_at);

        const currentStageId = getCurrentProjectStage(token.stages);

        const isDataUploadExist = checkExistingDataUpload(token.stages, currentStageId);

        // If data upload (stage report) was already done
        if (isDataUploadExist) {
          setLoading(false);
          setExistingReportModal(true);
          return;
        }

        const isFieldAgentExist = await checkExistingFieldAgent(token, currentStageId);

        if (isFieldAgentExist.error) {
          failFunc();
        } else {
          setTotalData(token);
          setProjectStatus(token.status?.toLowerCase());
          setCurrentStage(currentStageId);
          defineDataUpload(token, currentStageId);
          setFieldAgentExist(isFieldAgentExist.exist);
          setLoading(false);
        }
      } else failFunc();
    };

    if (account) {
      const bodyReq = {
        query: {
          bool: {
            must: [
              {
                match: {
                  'project_id.keyword': nameId,
                },
              },
              {
                match: {
                  'signer_id.keyword': account.accountId,
                },
              },
            ],
          },
        },
      };

      const { result, statusCode } = await sendRequest(indexerApi, 'POST', JSON.stringify(bodyReq), false);
      handleResponseStatus(
        statusCode,
        () => successFunc(result),
        () => failFunc(),
      );
    }
  }, [account]);

  if (loadingData) {
    return (
      <div className="data-upload__loader">
        <Loader />
      </div>
    );
  }

  // If first render request failed
  if (!Object.keys(totalData).length && showRequestModal) {
    return (
      <RequestModal
        error
        handleClose={() => history.push('/')}
        title={translate.formatMessage({ id: 'modal.title.oops' })}
        description={(
          <FormattedMessage
            id="modal.description.somethingGoesWrong"
            values={{
              link: (...chunks) => <a href="mailto:support@openforestprotocol.org" className="text-anchor">{chunks}</a>,
            }}
          />
)}
      />
    );
  }

  // If last stage is finished
  if (lastStageEndTime > 0 && lastStageEndTime < currentDate) {
    return (
      <RequestModal
        handleClose={() => history.push('/')}
        title={translate.formatMessage({ id: 'modal.title.congratulations' })}
        description={translate.formatMessage({ id: 'modal.description.finished' })}
      />
    );
  }

  // If stage report was done
  if (existingReportModal) {
    return (
      <RequestModal
        handleClose={() => history.push('/')}
        title={translate.formatMessage({ id: 'modal.title.congratulations' })}
        description={translate.formatMessage({ id: 'modal.title.sentToValidators' })}
      />
    );
  }

  if (!projectStatus) {
    return (
      <RequestModal
        error
        handleClose={() => history.push('/')}
        title={translate.formatMessage({ id: 'modal.title.wentWrong' })}
        description={translate.formatMessage({ id: 'modal.description.canNotTrack' })}
        buttonText={translate.formatMessage({ id: 'modal.button.return' })}
      />
    );
  }

  return (
    <>
      {showRequestModal && (
        <RequestModal
          error
          handleClose={() => setShowRequestModal(false)}
          title={translate.formatMessage({ id: 'modal.title.oops' })}
          description={(
            <FormattedMessage
              id="modal.description.somethingGoesWrong"
              values={{
                link: (...chunks) => <a href="mailto:support@openforestprotocol.org" className="text-anchor">{chunks}</a>,
              }}
            />
)}
        />
      )}
      {projectStatus === REGISTER_STATUS && (
        <SubzoneModal
          closeHandler={() => history.push('/')}
          submitHandler={() => handleMintProject()}
          closeBtnText={translate.formatMessage({ id: 'button-back' })}
          submitBtnText={translate.formatMessage({ id: 'button-confirm' })}
          modalText={(
            <FormattedMessage
              id="stageReport.modal.description.registerStatus"
              values={{
                span: (...chunks) => <span>{chunks}</span>,
                bold: (chunks) => <b>{chunks}</b>,
                br: <br />,
              }}
            />
          )}
        />
      )}
      {projectStatus === PAUSED_STATUS && (
        <SubzoneModal
          closeHandler={() => history.push('/')}
          submitHandler={() => history.push('/')}
          closeBtnText={translate.formatMessage({ id: 'button-back' })}
          submitBtnText={translate.formatMessage({ id: 'button-gotIt' })}
          modalText={translate.formatMessage({ id: 'stageReport.modal.description.pausedStatus' })}
        />
      )}
      {projectStatus === FAILED_STATUS && (
        <SubzoneModal
          closeHandler={() => history.push('/')}
          submitHandler={() => history.push('/')}
          closeBtnText={translate.formatMessage({ id: 'button-back' })}
          submitBtnText={translate.formatMessage({ id: 'button-gotIt' })}
          modalText={translate.formatMessage({ id: 'stageReport.modal.description.failedStatus' })}
        />
      )}
      {projectStatus === TERMINATED_STATUS && (
        <SubzoneModal
          closeHandler={() => history.push('/')}
          submitHandler={() => history.push('/')}
          closeBtnText={translate.formatMessage({ id: 'button-back' })}
          submitBtnText={translate.formatMessage({ id: 'button-gotIt' })}
          modalText={translate.formatMessage({ id: 'stageReport.modal.description.failedStatus' })}
        />
      )}
      {projectStatus === CLOSED_STATUS && (
        <SubzoneModal
          closeHandler={() => history.push('/')}
          submitHandler={() => history.push('/')}
          closeBtnText={translate.formatMessage({ id: 'button-back' })}
          submitBtnText={translate.formatMessage({ id: 'button-gotIt' })}
          modalText={translate.formatMessage({ id: 'stageReport.modal.description.endedStatus' })}
        />
      )}
      {projectStatus === ACTIVE_STATUS && currentStage === -1 && (
        <SubzoneModal
          closeHandler={() => history.push('/')}
          submitHandler={() => window.location.reload()}
          closeBtnText={translate.formatMessage({ id: 'button-back' })}
          submitBtnText={translate.formatMessage({ id: 'button-refresh' })}
          modalText={translate.formatMessage({ id: 'stageReport.modal.description.mintedStatus.start' })}
        />
      )}
      {projectStatus === ACTIVE_STATUS && currentStage >= 0 && (
        <>
          <section className="upload-wizard">
            <div className="upload-wizard__wrapper">
              <div className="upload-wizard__header">
                <div className="upload-wizard__logo">
                  <ReactSVG src={headerLogo} />
                </div>
                <h2 className="upload-wizard__title">
                  {translate.formatMessage({ id: 'dataUploadWizard.title' })}
                </h2>
                <div className="upload-wizard__cancel">
                  <CustomBtn
                    label={translate.formatMessage({ id: 'button-cancel' })}
                    customClass="btn__cancel"
                    iconClass="icon-close"
                    handleClick={() => {
                      history.push('/');
                    }}
                  />
                </div>
              </div>
              <div className="upload-wizard__body">
                <StepWizard step={step} stepForm={stepForm} showIcon={false} />
                {step === 0 && (
                  <MonitoringZone
                    totalData={totalData}
                    setTotalData={setTotalData}
                    nextPage={nextPage}
                    prevPage={() => history.goBack()}
                    currentStage={currentStage}
                    setNumberSampleZones={setNumberSampleZones}
                    projectReportType={projectReportType}
                    setProjectReportType={setProjectReportType}
                    firstDataUpload={firstDataUpload}
                    setShowRequestModal={setShowRequestModal}
                    fieldAgentExist={fieldAgentExist}
                  />
                )}
                {step === 1 && (
                  <SampleZones
                    totalData={totalData}
                    setTotalData={setTotalData}
                    nextPage={nextPage}
                    prevPage={prevPage}
                    currentStage={currentStage}
                    numberSampleZones={numberSampleZones}
                    handleUpdateProject={handleUpdateProject}
                    projectReportType={projectReportType}
                    firstDataUpload={firstDataUpload}
                    setShowRequestModal={setShowRequestModal}
                    fieldAgentExist={fieldAgentExist}
                  />
                )}
              </div>
            </div>
          </section>
          <Footer />
        </>
      )}
    </>
  );
};

export default React.memo(DataUploadWizard);
